<template>
  <form class="auth-step" @submit.prevent="handleSubmit">
    <h4 class="mb-3">Reset Password</h4>

    <!-- ═══ Step 1: Enter mobile number ═══ -->
    <template v-if="forgotDraft.step === 'mobile'">
      <p class="text-muted mb-20">Enter your registered mobile number to receive an OTP.</p>

      <div class="form-inner two mb-20">
        <label>Mobile No.</label>
        <div class="mobile-input-row">
          <span class="mobile-prefix">+91</span>
          <input
            :value="mobileDigits"
            type="tel"
            placeholder="Enter mobile number"
            maxlength="10"
            @input="onMobileInput"
          >
        </div>
      </div>

      <button type="submit" class="primary-btn1 w-100 mb-20" :disabled="sendingOtp">
        <span>{{ sendingOtp ? 'Sending OTP...' : 'Send OTP' }}</span>
      </button>
    </template>

    <!-- ═══ Step 2: Enter OTP + new password ═══ -->
    <template v-else-if="forgotDraft.step === 'otp'">
      <p class="text-muted mb-20">
        Enter the OTP sent to your mobile and set a new password.
      </p>

      <div v-if="forgotDraft.mockOtpHint" class="alert alert-info py-2 mb-20">
        Test OTP: <strong>{{ forgotDraft.mockOtpHint }}</strong>
      </div>

      <div class="form-inner two mb-20">
        <label>OTP</label>
        <input
          :value="forgotDraft.otp"
          type="text"
          placeholder="Enter 4-digit OTP"
          maxlength="4"
          inputmode="numeric"
          @input="updateField('otp', $event.target.value.replace(/\D/g, '').slice(0, 4))"
        >
        <div class="otp-meta mt-1">
          <span v-if="otpCountdownSeconds > 0" class="small text-muted">
            Expires in {{ otpCountdownSeconds }}s
          </span>
          <span v-else class="small text-danger">OTP expired</span>

          <button
            v-if="canResend"
            type="button"
            class="btn btn-link btn-sm p-0"
            @click="$emit('resend-otp')"
          >
            Resend OTP
          </button>
          <span v-else-if="resendCountdownSeconds > 0" class="small text-muted">
            Resend in {{ resendCountdownSeconds }}s
          </span>
        </div>
      </div>

      <div class="form-inner two mb-20">
        <label>New Password *</label>
        <div class="password-wrapper">
          <input
            :value="forgotDraft.password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="New password"
            autocomplete="new-password"
            @input="updateField('password', $event.target.value)"
          >
          <button type="button" class="password-toggle" @click="showPassword = !showPassword">
            <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
          </button>
        </div>
        <div class="password-hint">Min 6 characters, one uppercase letter, one special character.</div>
      </div>

      <div class="form-inner two mb-20">
        <label>Confirm Password *</label>
        <div class="password-wrapper">
          <input
            :value="forgotDraft.passwordConfirmation"
            :type="showConfirmPassword ? 'text' : 'password'"
            placeholder="Confirm new password"
            autocomplete="new-password"
            @input="updateField('passwordConfirmation', $event.target.value)"
          >
          <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
            <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
          </button>
        </div>
      </div>

      <button type="submit" class="primary-btn1 w-100 mb-20" :disabled="resetting">
        <span>{{ resetting ? 'Resetting...' : 'Reset Password' }}</span>
      </button>
    </template>

    <div v-if="errorMessage" class="alert alert-danger py-2 mb-20">{{ errorMessage }}</div>

    <button type="button" class="primary-btn1 black-bg w-100" @click="$emit('back')">
      <span>Back to Log in</span>
    </button>
  </form>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  forgotDraft: {
    type: Object,
    required: true
  },
  status: {
    type: String,
    default: 'idle'
  },
  errorMessage: {
    type: String,
    default: ''
  },
  canResend: {
    type: Boolean,
    default: false
  },
  otpCountdownSeconds: {
    type: Number,
    default: 0
  },
  resendCountdownSeconds: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['send-otp', 'resend-otp', 'reset-password', 'update:forgot-draft', 'back'])

const showPassword = ref(false)
const showConfirmPassword = ref(false)

const sendingOtp = computed(() => props.status === 'sending_otp')
const resetting = computed(() => props.status === 'resetting_password')

const mobileDigits = computed(() => {
  const digits = String(props.forgotDraft.mobile ?? '').replace(/\D/g, '')
  return digits.slice(0, 10)
})

const onMobileInput = (event) => {
  const digits = String(event.target.value ?? '').replace(/\D/g, '').slice(0, 10)
  updateField('mobile', digits)
}

const updateField = (field, value) => {
  emit('update:forgot-draft', {
    ...props.forgotDraft,
    [field]: value
  })
}

const handleSubmit = () => {
  if (props.forgotDraft.step === 'mobile') {
    emit('send-otp')
  } else {
    emit('reset-password')
  }
}
</script>

<style scoped>
.otp-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.password-hint {
  color: #888;
  font-size: 12px;
  margin-top: 4px;
}

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  color: #888;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

.password-toggle:hover {
  color: #333;
}

.mobile-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-prefix {
  font-size: 16px;
  font-weight: 500;
  color: var(--title-color, #222);
  flex-shrink: 0;
  line-height: 56px;
}
</style>
